import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import FrontendLayout from "../../components/frontend/FrontendLayout";
import { formatNumberIndian } from "../../utils/formatNumber";
import { formatShortPrice } from "../../utils/formatNumber";

// Add these utility functions near the top of your component, after the interfaces
const trackLandViewInSession = (landId: number): boolean => {
  const today = new Date().toDateString();
  const sessionKey = `landViews_${today}`;

  try {
    // Get today's views from sessionStorage
    const todayViews = JSON.parse(sessionStorage.getItem(sessionKey) || "{}");

    // Check if user already viewed this land today
    if (todayViews[landId]) {
      return false; // Already viewed today
    }

    // Mark this land as viewed today
    todayViews[landId] = true;
    sessionStorage.setItem(sessionKey, JSON.stringify(todayViews));

    return true; // New view for today
  } catch (error) {
    console.error("Error tracking land view in session:", error);
    return false;
  }
};

const getSessionViewsCount = (): number => {
  const today = new Date().toDateString();
  const sessionKey = `landViews_${today}`;

  try {
    const todayViews = JSON.parse(sessionStorage.getItem(sessionKey) || "{}");
    return Object.keys(todayViews).length;
  } catch (error) {
    console.error("Error reading session views:", error);
    return 0;
  }
};

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Testimonial {
  tm_id: number;
  tm_name: string;
  tm_designation: string;
  tm_image: string;
  tm_msg: string;
  tm_status: number;
  rating: string;
}

interface Blog {
  b_id: number;
  b_title: string;
  b_desc: string;
  b_pageurl: string;
  b_postdt: string;
  b_img: string;
  b_status: number;
}

interface Land {
  ln_id: number;
  ln_title: string;
  ln_price: number;
  ln_total_area_sqft: number;
  ln_total_sqft_area: number;
  ln_seo_url: string;
  ln_plottype: string;
  ln_locality: string;
  ln_address: string;
  ln_post_date: string;
  image_files: string[];
  ln_status: number;
  ln_admin_approve: number;
  ln_featured: string | null;
  ln_land_avail: string | null;
  ln_exclusive_owner_status: number;
  ln_corner: string;
  ln_nearby_landmark: string;
  ln_gated?: string;
  ln_facing?: string;
  ln_boundarywall?: string;
  ln_authority_approve?: string;
}

interface LocationItem {
  ln_locality: string;
}

interface PriceOption {
  label: string;
  value: string;
}

interface BudgetDropdownProps {
  minPrice: string;
  maxPrice: string;
  minPriceLabel: string;
  maxPriceLabel: string;
  setMinPrice: (value: string) => void;
  setMaxPrice: (value: string) => void;
  setMinPriceLabel: (label: string) => void;
  setMaxPriceLabel: (label: string) => void;
}

const HomePage: React.FC = () => {
  const [featuredPlots, setFeaturedPlots] = useState<Land[]>([]);
  const [exclusiveOwnerPlots, setExclusiveOwnerPlots] = useState<Land[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [userType, setUserType] = useState<number | null>(null);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [clientIp, setClientIp] = useState("");
  const [userId, setUserId] = useState<number | null>(null);
  const [recentViews, setRecentViews] = useState<number>(0);

  const [gatedCommunityCount, setGatedCommunityCount] = useState(0);
  const [eastFacingCount, setEastFacingCount] = useState(0);
  const [cornerPlotCount, setCornerPlotCount] = useState(0);
  const [boundaryWallCount, setBoundaryWallCount] = useState(0);
  const [bdaApprovedCount, setBdaApprovedCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [highestPrice, setHighestPrice] = useState(5000000000);

  // ✅ slider states
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(5000000000);

  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");

  const [activeTab, setActiveTab] = useState<"min" | "max">("min");
  const [minPriceLabel, setMinPriceLabel] = useState("Min Price");
  const [maxPriceLabel, setMaxPriceLabel] = useState("Max Price");

  // for styling range highlight
  const [percent1, setPercent1] = useState(0);
  const [percent2, setPercent2] = useState(100);

  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAll, setShowAll] = useState(false);

  // Plot type filter state
  const [selectedPlotTypes, setSelectedPlotTypes] = useState<string[]>([]);
  // Posted by filter state
  const [selectedPostedBy, setSelectedPostedBy] = useState<string>("");
  // Area filter state
  const [areaMin, setAreaMin] = useState<string>("");
  const [areaMax, setAreaMax] = useState<string>("");
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const priceOptions: PriceOption[] = [
    { label: "₹5 Lac", value: "500000" },
    { label: "₹10 Lac", value: "1000000" },
    { label: "₹20 Lac", value: "2000000" },
    { label: "₹30 Lac", value: "3000000" },
    { label: "₹40 Lac", value: "4000000" },
    { label: "₹50 Lac", value: "5000000" },
    { label: "₹60 Lac", value: "6000000" },
    { label: "₹70 Lac", value: "7000000" },
    { label: "₹80 Lac", value: "8000000" },
    { label: "₹90 Lac", value: "9000000" },
    { label: "₹1 Cr", value: "10000000" },
    { label: "₹1.2 Cr", value: "12000000" },
    { label: "₹1.4 Cr", value: "14000000" },
    { label: "₹1.6 Cr", value: "16000000" },
    { label: "₹1.8 Cr", value: "18000000" },
    { label: "₹2 Cr", value: "20000000" },
    { label: "₹2.3 Cr", value: "23000000" },
    { label: "₹2.6 Cr", value: "26000000" },
    { label: "₹3 Cr", value: "30000000" },
    { label: "₹3.5 Cr", value: "35000000" },
    { label: "₹4 Cr", value: "40000000" },
    { label: "₹4.5 Cr", value: "45000000" },
    { label: "₹5 Cr", value: "50000000" },
    { label: "₹10 Cr", value: "100000000" },
    { label: "₹20 Cr", value: "200000000" },
  ];

  const getMaxPriceOptions = () => {
    if (!minPrice) return priceOptions;
    const minIndex = priceOptions.findIndex(
      (option) => option.value === minPrice
    );
    return minIndex >= 0 ? priceOptions.slice(minIndex + 1) : priceOptions;
  };

  const handleMinPriceSelect = (option: { label: string; value: string }) => {
    setMinPrice(option.value);
    setMinPriceLabel(option.label);
    if (maxPrice && parseInt(maxPrice) <= parseInt(option.value)) {
      setMaxPrice("");
      setMaxPriceLabel("Max Price");
    }
  };

  const handleMaxPriceSelect = (option: { label: string; value: string }) => {
    setMaxPrice(option.value);
    setMaxPriceLabel(option.label);
  };

  const handleTabClick = (tab: "min" | "max") => {
    setActiveTab(tab);
  };

  // update percentages whenever values change
  useEffect(() => {
    const maxRange = 5000000000;
    setPercent1((minValue / maxRange) * 100);
    setPercent2((maxValue / maxRange) * 100);
  }, [minValue, maxValue]);

  const navigate = useNavigate();

  useEffect(() => {
    // Handle cloning for multiple carousels
    const carousels = document.querySelectorAll(".carousel.slide");
    carousels.forEach((carousel) => {
      const items = carousel.querySelectorAll(".carousel-item");
      items.forEach((el) => {
        const minPerSlide = 3;
        let next = el.nextElementSibling;
        for (let i = 1; i < minPerSlide; i++) {
          if (!next) next = items[0];
          const cloneChild = next.querySelector("li")?.cloneNode(true);
          if (cloneChild) {
            el.querySelector("ul")?.appendChild(cloneChild);
          }
          next = next.nextElementSibling;
        }
      });
    });
  }, [featuredPlots, testimonials]);

  useEffect(() => {
    const fetchFeaturedPlots = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/landadd/frontend-list`, {
          params: {
            limit: 1000000000, // Fetch enough lands to cover counts and slider
          },
        });
        if (res.data.success) {
          const plots: Land[] = res.data.landadds; // Rely on backend filtering for ln_status, ln_admin_approve, ln_land_avail

          // Featured and exclusive plots for slider
          const featured = plots.filter(
            (plot: Land) => plot.ln_featured === "Yes"
          );
          setFeaturedPlots(featured);

          const exclusive = plots.filter(
            (plot: Land) => plot.ln_exclusive_owner_status === 1
          );
          setExclusiveOwnerPlots(exclusive);

          // Set counts using pagination.total if available, or fallback to client-side filtering
          const totalPlots = res.data.pagination?.total || plots.length;
          setGatedCommunityCount(
            res.data.pagination?.gatedCount || // If backend provides specific counts
              plots.filter((p) => p.ln_gated?.toLowerCase() === "yes").length
          );
          setEastFacingCount(
            res.data.pagination?.eastFacingCount ||
              plots.filter((p) => p.ln_facing?.toLowerCase() === "east").length
          );
          setCornerPlotCount(
            res.data.pagination?.cornerCount ||
              plots.filter((p) => p.ln_corner?.toLowerCase() === "yes").length
          );
          setBoundaryWallCount(
            res.data.pagination?.boundaryWallCount ||
              plots.filter((p) => p.ln_boundarywall?.toLowerCase() === "yes")
                .length
          );
          setBdaApprovedCount(
            res.data.pagination?.bdaApprovedCount ||
              plots.filter(
                (p) => p.ln_authority_approve?.toLowerCase() === "yes"
              ).length
          );

          // Log for debugging
          console.log("Fetched plots:", plots.length, "Total:", totalPlots);
          console.log("Featured plots:", featured.length);
        } else {
          console.error("Failed to fetch plots");
        }
      } catch (error) {
        console.error("Error fetching plots:", error);
      }
    };

    const fetchBlogs = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/blogs/frontend-list`);
        if (res.data.success) {
          const activeBlogs = res.data.blogs.filter(
            (blog: Blog) => blog.b_status === 1
          );
          setBlogs(activeBlogs);
        } else {
          console.error("Failed to fetch blogs");
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
      }
    };

    const fetchTestimonials = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/testimonials/frontend/list`
        );
        if (res.data.success) {
          const activeTestimonials = res.data.testimonials.filter(
            (t: Testimonial) => t.tm_status === 1
          );
          setTestimonials(activeTestimonials);
        } else {
          console.error("Failed to fetch testimonials");
        }
      } catch (error) {
        console.error("Error fetching testimonials", error);
      }
    };

    fetchFeaturedPlots();
    fetchBlogs();
    fetchTestimonials();
  }, []);

  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem("frontendUserToken");
      const name = localStorage.getItem("frontendUserName");
      const type = localStorage.getItem("frontendUserType");
      const id = localStorage.getItem("frontendUserId");
      const ip = localStorage.getItem("frontendUserIp");

      if (token && name) {
        setIsLoggedIn(true);
        setUserName(name);
        if (type) {
          setUserType(Number(type));
        }
        if (id) {
          setUserId(Number(id));
        }
        if (ip) {
          setClientIp(ip);
        }
      } else {
        setIsLoggedIn(false);
        setUserName("");
        setUserType(null);
        setUserId(null);
        setClientIp("");
      }
    };

    checkLoginStatus();

    const handleStorageChange = () => checkLoginStatus();
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("loginStatusChanged", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("loginStatusChanged", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      if (isLoggedIn && userId) {
        try {
          const token = localStorage.getItem("frontendUserToken"); // saved at login

          const res = await axios.get(`${API_BASE}/api/dashboard/home-page`, {
            params: { userId },
            headers: {
              Authorization: `Bearer ${token}`, // 👈 pass token
            },
          });

          if (res.data.success) {
            const stats = res.data.stats;
            setWishlistCount(stats.favorites || 0);
            setRecentViews(stats.viewed || 0);
          } else {
            setWishlistCount(0);
            setRecentViews(0);
          }
        } catch (error) {
          console.error("Error fetching dashboard stats:", error);
          setWishlistCount(0);
          setRecentViews(0);
        }
      }
    };

    fetchDashboardStats();
  }, [isLoggedIn, userId]);

  //helper for count the viewed count
  const getRecentViewsCount = (): number => {
    const sessionViews = getSessionViewsCount();
    return recentViews + sessionViews;
  };

  const getUserTypeLabel = () => {
    switch (userType) {
      case 1:
        return "Owner";
      case 2:
        return "Agent";
      case 3:
        return "Buyer";
      default:
        return "";
    }
  };

  // Function to render dynamic stars based on rating
  const renderStars = (rating: string) => {
    const numericRating = parseFloat(rating);
    const fullStars = Math.floor(numericRating);
    const hasHalfStar = numericRating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <>
        {/* Filled stars */}
        {[...Array(fullStars)].map((_, i) => (
          <i key={`full-${i}`} className="icon-star filled"></i>
        ))}

        {/* Half star (if needed) */}
        {hasHalfStar && <i key="half" className="icon-star half-filled"></i>}

        {/* Empty stars */}
        {[...Array(emptyStars)].map((_, i) => (
          <i key={`empty-${i}`} className="icon-star empty"></i>
        ))}
      </>
    );
  };

  const fetchLocations = async (search: string = "", all: boolean = false) => {
    try {
      const res = await axios.get(`${API_BASE}/api/alllandmaster/locations`, {
        params: { search, showAll: all },
      });

      if (res.data.success) {
        setLocations(res.data.locations);
      } else {
        setLocations([]);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
    }
  };

  useEffect(() => {
    fetchLocations(); // load first 10 initially
  }, []);

  // 🔹 Handle search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchLocations(value, false); // search but still show only 10
  };

  const handleLocationChange = (locality: string) => {
    setSelectedLocations((prev) => {
      if (prev.includes(locality)) {
        return prev.filter((loc) => loc !== locality);
      } else {
        return [...prev, locality];
      }
    });
  };

  // 🔹 Load all when user clicks "Show more"
  const handleShowAll = () => {
    fetchLocations(searchTerm, true);
    setShowAll(true);
  };

  const handleCityClick = (city: string) => {
    navigate(`/lands?locality=${encodeURIComponent(city)}`);
  };

  const handlePlotTypeChange = (type: string) => {
    setSelectedPlotTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handlePostedByChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPostedBy(e.target.value);
  };

  const handleAreaMinChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAreaMin(e.target.value);
  };

  const handleAreaMaxChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAreaMax(e.target.value);
  };

  const handleSearch = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    e.preventDefault();
    const queryParams = new URLSearchParams();

    if (selectedLocations.length > 0) {
      queryParams.append("locality", selectedLocations.join(","));
    } else if (searchTerm) {
      queryParams.append("locality", encodeURIComponent(searchTerm));
    }
    if (selectedPlotTypes.length > 0) {
      queryParams.append("type", selectedPlotTypes.join(","));
    }
    if (minPrice) {
      queryParams.append("minPrice", minPrice);
    }
    if (maxPrice) {
      queryParams.append("maxPrice", maxPrice);
    }
    if (areaMin) {
      queryParams.append("minArea", areaMin);
    }
    if (areaMax) {
      queryParams.append("maxArea", areaMax);
    }
    if (selectedPostedBy) {
      queryParams.append("ownerType", selectedPostedBy === "owner" ? "1" : "2");
    }

    navigate(`/lands?${queryParams.toString()}`);
    setIsMobileFilterOpen(false);
  };

  // Budget dropdown JSX with new tabbed interface
  // Budget dropdown JSX with new tabbed interface - FIXED VERSION
  const BudgetModal: React.FC<BudgetDropdownProps> = ({
    minPrice,
    maxPrice,
    minPriceLabel,
    maxPriceLabel,
    setMinPrice,
    setMaxPrice,
    setMinPriceLabel,
    setMaxPriceLabel,
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempMinPrice, setTempMinPrice] = useState(minPrice);
    const [tempMaxPrice, setTempMaxPrice] = useState(maxPrice);

    const getMaxPriceOptions = () => {
      if (!tempMinPrice) return priceOptions;
      const minIndex = priceOptions.findIndex(
        (option) => option.value === tempMinPrice
      );
      return minIndex >= 0 ? priceOptions.slice(minIndex + 1) : priceOptions;
    };

    const handleApply = () => {
      setMinPrice(tempMinPrice);
      setMaxPrice(tempMaxPrice);

      const minLabel = tempMinPrice
        ? priceOptions.find((opt) => opt.value === tempMinPrice)?.label ||
          "Min Price"
        : "Min Price";
      const maxLabel = tempMaxPrice
        ? priceOptions.find((opt) => opt.value === tempMaxPrice)?.label ||
          "Max Price"
        : "Max Price";

      setMinPriceLabel(minLabel);
      setMaxPriceLabel(maxLabel);
      setIsOpen(false);
    };

    const handleCancel = () => {
      setTempMinPrice(minPrice);
      setTempMaxPrice(maxPrice);
      setIsOpen(false);
    };

    const getBudgetDisplayText = () => {
      if (minPrice && maxPrice) {
        return `${minPriceLabel} - ${maxPriceLabel}`;
      } else if (minPrice) {
        return `From ${minPriceLabel}`;
      } else if (maxPrice) {
        return `Up to ${maxPriceLabel}`;
      }
      return "Budget";
    };

    return (
      <>
        <div className="dropdown tf-dropdown-sort">
          <button
            className="btn custom-dropdown dropdown-toggle btn-select"
            type="button"
            onClick={() => setIsOpen(true)}
          >
            <span className="text-sort-value">{getBudgetDisplayText()}</span>
            <i className="icon-CaretDown"></i>
          </button>
        </div>

        {isOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            onClick={handleCancel}
          >
            <div
              style={{
                background: "white",
                padding: "24px",
                borderRadius: "8px",
                minWidth: "320px",
                maxHeight: "80vh",
                overflowY: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: "0 0 20px 0", fontSize: "18px" }}>
                Select Budget Range
              </h3>

              <div className="d-flex justify-content-between">
                <div style={{ marginBottom: "20px", width: "100%" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    Min Price
                  </label>
                  <select
                    value={tempMinPrice}
                    onChange={(e) => {
                      setTempMinPrice(e.target.value);
                      if (
                        tempMaxPrice &&
                        parseInt(tempMaxPrice) <= parseInt(e.target.value)
                      ) {
                        setTempMaxPrice("");
                      }
                    }}
                    className="form-select nice-select"
                  >
                    <option value="">Select</option>
                    {priceOptions.map((option) => (
                      <option
                        key={`modal-min-${option.value}`}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div
                  style={{
                    marginBottom: "24px",
                    width: "100%",
                    marginLeft: "10px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    Max Price
                  </label>
                  <select
                    value={tempMaxPrice}
                    onChange={(e) => setTempMaxPrice(e.target.value)}
                    className="form-select nice-select"
                  >
                    <option value="">Select</option>
                    {getMaxPriceOptions().map((option) => (
                      <option
                        key={`modal-max-${option.value}`}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={handleCancel}
                  className="minmax tf-btn style-border pd-2"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  className="minmax btn-sm tf-btn bg-color-primary pd-2"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Mobile Filter Popup Component
  const MobileFilterPopup: React.FC = () => {
    const [tempMinPrice, setTempMinPrice] = useState(minPrice);
    const [tempMaxPrice, setTempMaxPrice] = useState(maxPrice);
    const [tempSelectedLocations, setTempSelectedLocations] =
      useState<string[]>(selectedLocations);
    const [tempSelectedPlotTypes, setTempSelectedPlotTypes] =
      useState<string[]>(selectedPlotTypes);
    const [tempSelectedPostedBy, setTempSelectedPostedBy] =
      useState<string>(selectedPostedBy);
    const [tempAreaMin, setTempAreaMin] = useState<string>(areaMin);
    const [tempAreaMax, setTempAreaMax] = useState<string>(areaMax);
    const [tempSearchTerm, setTempSearchTerm] = useState(searchTerm);
    const [filteredLocations, setFilteredLocations] =
      useState<LocationItem[]>(locations);

    // Store all locations in a ref to avoid re-renders
    const allLocationsRef = useRef<LocationItem[]>([]);

    // Load all locations once when component mounts
    useEffect(() => {
      const loadAllLocations = async () => {
        try {
          const res = await axios.get(
            `${API_BASE}/api/alllandmaster/locations`,
            {
              params: { showAll: true },
            }
          );
          if (res.data.success) {
            allLocationsRef.current = res.data.locations;
            setFilteredLocations(res.data.locations.slice(0, 10)); // Show first 10 initially
          }
        } catch (error) {
          console.error("Error loading all locations:", error);
        }
      };

      loadAllLocations();
    }, []);

    // Filter locations based on search term without API calls
    useEffect(() => {
      if (allLocationsRef.current.length > 0) {
        if (tempSearchTerm) {
          const filtered = allLocationsRef.current.filter((loc) =>
            loc.ln_locality.toLowerCase().includes(tempSearchTerm.toLowerCase())
          );
          setFilteredLocations(filtered);
        } else {
          setFilteredLocations(allLocationsRef.current.slice(0, 10));
        }
      }
    }, [tempSearchTerm]);

    const getMaxPriceOptions = () => {
      if (!tempMinPrice) return priceOptions;
      const minIndex = priceOptions.findIndex(
        (option) => option.value === tempMinPrice
      );
      return minIndex >= 0 ? priceOptions.slice(minIndex + 1) : priceOptions;
    };

    const handleApply = () => {
      // Update main state
      setMinPrice(tempMinPrice);
      setMaxPrice(tempMaxPrice);
      setSelectedLocations(tempSelectedLocations);
      setSelectedPlotTypes(tempSelectedPlotTypes);
      setSelectedPostedBy(tempSelectedPostedBy);
      setAreaMin(tempAreaMin);
      setAreaMax(tempAreaMax);
      setSearchTerm(tempSearchTerm);

      const minLabel = tempMinPrice
        ? priceOptions.find((opt) => opt.value === tempMinPrice)?.label ||
          "Min Price"
        : "Min Price";
      const maxLabel = tempMaxPrice
        ? priceOptions.find((opt) => opt.value === tempMaxPrice)?.label ||
          "Max Price"
        : "Max Price";
      setMinPriceLabel(minLabel);
      setMaxPriceLabel(maxLabel);

      setIsMobileFilterOpen(false);

      // Build query params using temp values (current filter selections)
      const queryParams = new URLSearchParams();

      if (tempSelectedLocations.length > 0) {
        queryParams.append("locality", tempSelectedLocations.join(","));
      } else if (tempSearchTerm) {
        queryParams.append("locality", encodeURIComponent(tempSearchTerm));
      }
      if (tempSelectedPlotTypes.length > 0) {
        queryParams.append("type", tempSelectedPlotTypes.join(","));
      }
      if (tempMinPrice) {
        queryParams.append("minPrice", tempMinPrice);
      }
      if (tempMaxPrice) {
        queryParams.append("maxPrice", tempMaxPrice);
      }
      if (tempAreaMin) {
        queryParams.append("minArea", tempAreaMin);
      }
      if (tempAreaMax) {
        queryParams.append("maxArea", tempAreaMax);
      }
      if (tempSelectedPostedBy) {
        queryParams.append(
          "ownerType",
          tempSelectedPostedBy === "owner" ? "1" : "2"
        );
      }

      // Navigate with the built query params
      navigate(`/lands?${queryParams.toString()}`);
    };

    const handleCancel = () => {
      setTempMinPrice(minPrice);
      setTempMaxPrice(maxPrice);
      setTempSelectedLocations(selectedLocations);
      setTempSelectedPlotTypes(selectedPlotTypes);
      setTempSelectedPostedBy(selectedPostedBy);
      setTempAreaMin(areaMin);
      setTempAreaMax(areaMax);
      setTempSearchTerm(searchTerm);
      setIsMobileFilterOpen(false);
    };

    const handleMobileSearchChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      setTempSearchTerm(e.target.value);
      // No API call here - we filter client-side using the useEffect
    };

    const handleMobileLocationChange = (locality: string) => {
      setTempSelectedLocations((prev) =>
        prev.includes(locality)
          ? prev.filter((loc) => loc !== locality)
          : [...prev, locality]
      );
    };

    const handleMobilePlotTypeChange = (type: string) => {
      setTempSelectedPlotTypes((prev) =>
        prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
      );
    };

    const handleShowAllMobile = () => {
      setFilteredLocations(allLocationsRef.current);
    };

    return (
      <>
        {isMobileFilterOpen && (
          <div
            className="popup"
            id="popupBox"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            onClick={handleCancel}
          >
            <div
              className="popup-content"
              style={{
                background: "white",
                padding: "24px",
                borderRadius: "8px",
                width: "90%",
                maxHeight: "80vh",
                overflowY: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="section-title mb-3 w-100">Plot Type</div>
              <div className="mb-3">
                {[
                  "Residential",
                  "Commercial",
                  "Agricultural",
                  "Industrial",
                ].map((type) => (
                  <div className="form-check" key={type}>
                    <input
                      className="form-check-input location-checkbox"
                      type="checkbox"
                      id={`mobile-plot-${type}`}
                      checked={tempSelectedPlotTypes.includes(type)}
                      onChange={() => handleMobilePlotTypeChange(type)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`mobile-plot-${type}`}
                    >
                      <span className="text-4">{type}</span>
                    </label>
                  </div>
                ))}
              </div>

              <div className="section-title mb-3 pt-4 w-100">Area (Sqft)</div>
              <div className="d-flex justify-content-between">
                <select
                  id="mobileAreaMin"
                  className="dropdown-select form-select text-4 me-2"
                  value={tempAreaMin}
                  onChange={(e) => setTempAreaMin(e.target.value)}
                >
                  <option value="">Min</option>
                  <option>100</option>
                  <option>200</option>
                  <option>300</option>
                  <option>400</option>
                  <option>500</option>
                  <option>1000</option>
                </select>
                <select
                  id="mobileAreaMax"
                  className="dropdown-select form-select text-4"
                  value={tempAreaMax}
                  onChange={(e) => setTempAreaMax(e.target.value)}
                >
                  <option value="">Max</option>
                  <option>1000</option>
                  <option>2000</option>
                  <option>3000</option>
                  <option>4000</option>
                  <option>5000</option>
                </select>
              </div>

              <div className="section-title mb-3 pt-4 w-100">Posted By</div>
              <div className="radio-btns">
                <input
                  type="radio"
                  name="mobilePlan"
                  id="mobileOwner"
                  value="owner"
                  checked={tempSelectedPostedBy === "owner"}
                  onChange={(e) => setTempSelectedPostedBy(e.target.value)}
                />
                <label htmlFor="mobileOwner">Owner</label>
                <input
                  type="radio"
                  name="mobilePlan"
                  id="mobileAgent"
                  value="agent"
                  checked={tempSelectedPostedBy === "agent"}
                  onChange={(e) => setTempSelectedPostedBy(e.target.value)}
                />
                <label htmlFor="mobileAgent">Agent</label>
              </div>

              <div className="section-title mb-3 pt-4 w-100">Budget</div>
              <div className="d-flex justify-content-between">
                <div style={{ marginBottom: "20px", width: "100%" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    Min Price
                  </label>
                  <select
                    value={tempMinPrice}
                    onChange={(e) => {
                      setTempMinPrice(e.target.value);
                      if (
                        tempMaxPrice &&
                        parseInt(tempMaxPrice) <= parseInt(e.target.value)
                      ) {
                        setTempMaxPrice("");
                      }
                    }}
                    className="form-select nice-select"
                  >
                    <option value="">Select</option>
                    {priceOptions.map((option) => (
                      <option
                        key={`mobile-min-${option.value}`}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  style={{
                    marginBottom: "24px",
                    width: "100%",
                    marginLeft: "10px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    Max Price
                  </label>
                  <select
                    value={tempMaxPrice}
                    onChange={(e) => setTempMaxPrice(e.target.value)}
                    className="form-select nice-select"
                  >
                    <option value="">Select</option>
                    {getMaxPriceOptions().map((option) => (
                      <option
                        key={`mobile-max-${option.value}`}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="section-title mb-3 pt-4 w-100">Location</div>
              <input
                type="text"
                className="search-box mb-2"
                id="mobileLocationSearch"
                placeholder="Search locations"
                value={tempSearchTerm}
                onChange={handleMobileSearchChange}
              />
              {filteredLocations.map((loc, index) => (
                <div className="form-check" key={index}>
                  <input
                    className="form-check-input location-checkbox"
                    type="checkbox"
                    id={`mobile-loc-${index}`}
                    checked={tempSelectedLocations.includes(loc.ln_locality)}
                    onChange={() => handleMobileLocationChange(loc.ln_locality)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`mobile-loc-${index}`}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="text-4">{loc.ln_locality}</span>
                  </label>
                </div>
              ))}
              {!tempSearchTerm &&
                filteredLocations.length === 10 &&
                allLocationsRef.current.length > 10 && (
                  <button
                    className="btn btn-link mt-2 p-0"
                    onClick={handleShowAllMobile}
                  >
                    Show more...
                  </button>
                )}

              <div className="d-flex justify-content-center pt-5">
                <button
                  className="minmax tf-btn style-border pd-2 me-2"
                  onClick={handleCancel}
                >
                  <i
                    style={{ fontSize: "18px" }}
                    className="fa fa-arrow-left"
                  />
                  Cancel
                </button>
                <button
                  className="minmax btn-sm tf-btn bg-color-primary pd-2"
                  onClick={handleApply}
                >
                  <i style={{ fontSize: "18px" }} className="fa fa-search" />
                  Search
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <FrontendLayout>
      {/* CSS inside component */}
      <style>{`
        .radio-btns {
          display: flex;
          gap: 10px;
        }

        .radio-btns input[type="radio"] {
          display: none; /* hide native */
        }

        .radio-btns label {
          padding: 8px 18px;
          border: 2px solid #5a2ca0;
          border-radius: 6px;
          color: #5a2ca0;
          font-weight: 600;
          cursor: pointer;
          transition: all .2s ease;
        }

        .radio-btns label:hover {
          background: #f3eaff;
        }

        .radio-btns input[type="radio"]:checked + label {
          background: #824b76;
          color: #fff;
        }
      `}</style>

      {/* Page Title Section */}
      <div className="page-title home01">
        <div className="tf-container">
          <div className="row relative">
            <div className="col-lg-7">
              <div className="content-inner pt-5">
                <div className="heading-title">
                  <h1 className="title">
                    Find Your <br />
                    Perfect Land.
                  </h1>
                  {/* <p className="h6 fw-4">
                    Thousands of luxury home enthusiasts just like you visit our
                    website.
                  </p> */}
                </div>
                <div className="wg-filter">
                  {/* Mobile Filter */}
                  <div className="form-title d-flex justify-content-between d-block d-sm-none">
                    <div
                      className="input-wrapper"
                      style={{ position: "relative" }}
                    >
                      <input
                        type="text"
                        id="popupInput"
                        placeholder="Search by Location, Plot Type, Budget"
                        className="rounded-pill"
                        onClick={() => setIsMobileFilterOpen(true)}
                        style={{ paddingRight: "40px" }}
                      />
                      <i
                        className="fa fa-search search-icon"
                        style={{
                          position: "absolute",
                          right: "15px",
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      ></i>
                    </div>
                    <MobileFilterPopup />
                  </div>
                  <div className="d-none d-sm-block">
                    <div className="form-title d-flex justify-content-between">
                      <div className="dropdown tf-dropdown-sort">
                        <button
                          className="btn custom-dropdown dropdown-toggle btn-select"
                          type="button"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                        >
                          <span className="text-sort-value">Plot Type</span>
                          <i className="icon-CaretDown"></i>
                        </button>
                        <div className="dropdown-menu p-3">
                          <div className="mb-3">
                            <div className="form-check">
                              <input
                                className="form-check-input location-checkbox"
                                type="checkbox"
                                id="plotResidential"
                                checked={selectedPlotTypes.includes(
                                  "Residential"
                                )}
                                onChange={() =>
                                  handlePlotTypeChange("Residential")
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="plotResidential"
                              >
                                <span className="text-4">Residential</span>
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input location-checkbox"
                                type="checkbox"
                                id="plotCommercial"
                                checked={selectedPlotTypes.includes(
                                  "Commercial"
                                )}
                                onChange={() =>
                                  handlePlotTypeChange("Commercial")
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="plotCommercial"
                              >
                                <span className="text-4">Commercial</span>
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input location-checkbox"
                                type="checkbox"
                                id="plotAgricultural"
                                checked={selectedPlotTypes.includes(
                                  "Agricultural"
                                )}
                                onChange={() =>
                                  handlePlotTypeChange("Agricultural")
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="plotAgricultural"
                              >
                                <span className="text-4">Agricultural</span>
                              </label>
                            </div>
                            <div className="form-check">
                              <input
                                className="form-check-input location-checkbox"
                                type="checkbox"
                                id="plotIndustrial"
                                checked={selectedPlotTypes.includes(
                                  "Industrial"
                                )}
                                onChange={() =>
                                  handlePlotTypeChange("Industrial")
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="plotIndustrial"
                              >
                                <span className="text-4">Industrial</span>
                              </label>
                            </div>
                          </div>
                          <div className="mb-3">
                            <div className="section-title mb-2 fw-bold">
                              Area (Sqft)
                            </div>
                            <div className="d-flex justify-content-between">
                              <select
                                id="areaMin"
                                className="dropdown-select form-select text-4 me-2"
                                value={areaMin}
                                onChange={handleAreaMinChange}
                              >
                                <option value="">Min</option>
                                <option>100</option>
                                <option>200</option>
                                <option>300</option>
                                <option>400</option>
                                <option>500</option>
                                <option>1000</option>
                              </select>
                              <select
                                id="areaMax"
                                className="dropdown-select form-select text-4"
                                value={areaMax}
                                onChange={handleAreaMaxChange}
                              >
                                <option value="">Max</option>
                                <option>1000</option>
                                <option>2000</option>
                                <option>3000</option>
                                <option>4000</option>
                                <option>5000</option>
                              </select>
                            </div>
                          </div>
                          <div className="mb-2">
                            <div className="section-title mb-2 fw-bold">
                              Posted By
                            </div>
                            <div className="radio-btns">
                              <input
                                type="radio"
                                name="plan"
                                id="owner"
                                value="owner"
                                checked={selectedPostedBy === "owner"}
                                onChange={handlePostedByChange}
                              />
                              <label htmlFor="owner">Owner</label>
                              <input
                                type="radio"
                                name="plan"
                                id="agent"
                                value="agent"
                                checked={selectedPostedBy === "agent"}
                                onChange={handlePostedByChange}
                              />
                              <label htmlFor="agent">Agent</label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="dropdown tf-dropdown-sort">
                        <button
                          className="btn custom-dropdown dropdown-toggle btn-select"
                          type="button"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                        >
                          <span className="text-sort-value">Location</span>
                          <i className="icon-CaretDown"></i>
                        </button>
                        <div className="dropdown-menu px-3 py-2">
                          <input
                            type="text"
                            className="search-box mb-2"
                            id="locationSearch"
                            placeholder="Search locations"
                            value={searchTerm}
                            onChange={handleSearchChange}
                          />
                          {locations.map((loc, index) => (
                            <div className="form-check" key={index}>
                              <input
                                className="form-check-input location-checkbox"
                                type="checkbox"
                                id={`loc-${index}`}
                                checked={selectedLocations.includes(
                                  loc.ln_locality
                                )}
                                onChange={() =>
                                  handleLocationChange(loc.ln_locality)
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`loc-${index}`}
                                style={{ cursor: "pointer" }}
                              >
                                <span className="text-4">
                                  {loc.ln_locality}
                                </span>
                              </label>
                            </div>
                          ))}
                          {/* {!showAll && locations.length === 10 && (
                          <button
                            className="btn btn-link mt-2 p-0"
                            onClick={handleShowAll}
                          >
                            Show more...
                          </button>
                        )} */}
                        </div>
                      </div>
                      <BudgetModal
                        minPrice={minPrice}
                        maxPrice={maxPrice}
                        minPriceLabel={minPriceLabel}
                        maxPriceLabel={maxPriceLabel}
                        setMinPrice={setMinPrice}
                        setMaxPrice={setMaxPrice}
                        setMinPriceLabel={setMinPriceLabel}
                        setMaxPriceLabel={setMaxPriceLabel}
                      />
                      <div className="box-item wrap-btn">
                        <a
                          href="#"
                          className="tf-btn bg-color-primary pd-3"
                          onClick={handleSearch}
                        >
                          Search <i className="icon-MagnifyingGlass fw-6"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-5 d-none d-sm-block">
              {/* <img
                className="lazyload"
                src={`${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/banner-img.jpg`}
                alt=""
              /> */}
              <video
                src={`${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/land.mp4`}
                width="640"
                height="360"
                autoPlay
                muted
                loop
                playsInline
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div>
        {/* Categories Section */}
        <div className="main-section-bg">
          <section className="tf-spacing-1 section-categories pb-0">
            <div className="tf-container">
              <div className="heading-section text-center mb-48">
                <h2 className="title split-text effect-right">
                  Pick your perfect piece of plot
                </h2>
                <p className="text-1 split-text split-lines-transform">
                  Thousands of luxury home enthusiasts just like you have found
                  their dream home
                </p>
              </div>

              <ul className="responsive-list">
                <li>
                  <Link to="/lands?gated=Yes" className="categories-item sec1">
                    <div className="icon-box">
                      <i
                        className="fas fa-door-open"
                        style={{ fontSize: "4rem" }}
                      ></i>
                    </div>
                    <div className="content text-center">
                      <h5>Plots in Gated Community</h5>
                      <p className="mt-4 text-1">
                        {gatedCommunityCount}{" "}
                        {gatedCommunityCount === 1 ? "Plot" : "Plots"}
                      </p>
                    </div>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/lands?facing=East"
                    className="categories-item sec2"
                  >
                    <div className="icon-box">
                      <i
                        className="fa fa-compass"
                        style={{ fontSize: "4rem" }}
                      ></i>
                    </div>
                    <div className="content text-center">
                      <h5>East Facing Plots</h5>
                      <p className="mt-4 text-1">
                        {eastFacingCount}{" "}
                        {eastFacingCount === 1 ? "Plot" : "Plots"}
                      </p>
                    </div>
                  </Link>
                </li>
                <li>
                  <Link to="/lands?corner=Yes" className="categories-item sec3">
                    <div className="icon-box">
                      <i
                        className="fa fa-ruler-combined"
                        style={{ fontSize: "4rem" }}
                      ></i>
                    </div>
                    <div className="content text-center">
                      <h5>Corner Plots</h5>
                      <p className="mt-4 text-1">
                        {cornerPlotCount}{" "}
                        {cornerPlotCount === 1 ? "Plot" : "Plots"}
                      </p>
                    </div>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/lands?boundarywall=Yes"
                    className="categories-item sec4"
                  >
                    <div className="icon-box">
                      <i
                        className="fas fa-border-style"
                        style={{ fontSize: "4rem" }}
                      ></i>
                    </div>
                    <div className="content text-center">
                      <h5>Plots with Boundary Wall</h5>
                      <p className="mt-4 text-1">
                        {boundaryWallCount}{" "}
                        {boundaryWallCount === 1 ? "Plot" : "Plots"}
                      </p>
                    </div>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/lands?authorityApprove=Yes"
                    className="categories-item"
                  >
                    <div className="icon-box">
                      <i
                        className="fas fa-check-circle"
                        style={{ fontSize: "4rem" }}
                      ></i>
                    </div>
                    <div className="content text-center">
                      <h5>Authority Approved</h5>
                      <p className="mt-4 text-1">
                        {bdaApprovedCount}{" "}
                        {bdaApprovedCount === 1 ? "Plot" : "Plots"}
                      </p>
                    </div>
                  </Link>
                </li>
              </ul>
            </div>
          </section>

          {/* Featured Plots Section Desktop View */}
          {featuredPlots.length > 0 && (
            <div className="d-none d-sm-block">
              <section className="section-neighborhoods style-2 tf-spacing-1">
                <div className="tf-container">
                  <div className="row">
                    <div className="col-12">
                      <div className="heading-section mb-46 text-center">
                        <h2 className="title split-text effect-right">
                          Featured Plots
                        </h2>
                      </div>

                      <div
                        id="multiCarousel1"
                        className="carousel slide mb-5"
                        data-bs-ride="carousel"
                      >
                        <div className="carousel-inner">
                          {(() => {
                            const groupedPlots = [];
                            for (let i = 0; i < featuredPlots.length; i += 3) {
                              groupedPlots.push(featuredPlots.slice(i, i + 3));
                            }
                            return groupedPlots.map((group, groupIndex) => (
                              <div
                                className={`carousel-item ${
                                  groupIndex === 0 ? "active" : ""
                                }`}
                                key={groupIndex}
                              >
                                <ul className="carousel-list">
                                  {group.map((plot) => (
                                    <li key={plot.ln_id}>
                                      <div className="box-location hover-img">
                                        <div className="plot-location">
                                          <i className="bi bi-geo-alt me-1"></i>
                                          <div className="plot-location-text ms-2">
                                            {plot.ln_locality},<br />{" "}
                                            {plot.ln_address}
                                          </div>
                                        </div>
                                        <div className="plot-details">
                                          <div className="plot-details-row">
                                            {plot.ln_total_sqft_area} sqft
                                            <br />
                                            Plot Area
                                          </div>
                                          <div className="plot-details-row">
                                            {plot.ln_plottype}
                                          </div>
                                          <div className="plot-details-row no-border">
                                            {new Date(
                                              plot.ln_post_date
                                            ).toLocaleDateString("en-US", {
                                              day: "2-digit",
                                              month: "long",
                                              year: "numeric",
                                            })}
                                            <br />
                                            Posted on
                                          </div>
                                        </div>
                                        <div className="image-wrap">
                                          <Link to={`/${plot.ln_seo_url}`}>
                                            <img
                                              className="lazyload"
                                              src={
                                                plot.image_files?.length > 0
                                                  ? `${API_BASE}/uploads/lands/${plot.image_files[0]}`
                                                  : `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`
                                              }
                                              alt={plot.ln_title}
                                            />
                                          </Link>
                                        </div>
                                        <div className="content">
                                          <h4 className="text_white">
                                            <i className="bi bi-currency-rupee me-1"></i>
                                            {formatShortPrice(plot.ln_price)}
                                            <br />
                                            <small>
                                              <i className="bi bi-currency-rupee me-1"></i>
                                              {formatNumberIndian(
                                                plot.ln_total_area_sqft
                                              )}{" "}
                                              per sqft
                                            </small>
                                          </h4>
                                        </div>
                                        {/* <Link
                                          to={`/${plot.ln_seo_url}`}
                                          className="save"
                                        >
                                          <i className="bi bi-heart"></i>
                                        </Link> */}
                                        <Link
                                          to={`/${plot.ln_seo_url}`}
                                          className="click"
                                        >
                                          <i className="bi bi-arrow-up-right-circle-fill"></i>
                                        </Link>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ));
                          })()}
                        </div>
                        <div className="carousel-controls-center">
                          <button
                            className="carousel-control-prev swiper-button-next sw-button"
                            type="button"
                            data-bs-target="#multiCarousel1"
                            data-bs-slide="prev"
                          >
                            <i className="icon-arrow-left-3"></i>
                          </button>
                          <button
                            className="carousel-control-next swiper-button-next sw-button"
                            type="button"
                            data-bs-target="#multiCarousel1"
                            data-bs-slide="next"
                          >
                            <i className="icon-arrow-right-3"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}
          {/* Featured Plots Section Mobile View */}
          {featuredPlots.length > 0 && (
            <div className="mob-s d-block d-sm-none">
              <div className="container py-5">
                <div
                  id="singleCardCarousel"
                  className="carousel slide"
                  data-bs-ride="carousel"
                >
                  <div className="carousel-inner">
                    {featuredPlots.map((plot, index) => (
                      <div
                        className={`carousel-item ${
                          index === 0 ? "active" : ""
                        }`}
                        key={plot.ln_id}
                      >
                        <div className="box-location hover-img">
                          <div className="plot-location">
                            <i className="bi bi-geo-alt me-1"></i>
                            <div className="plot-location-text ms-2">
                              {plot.ln_locality},<br />
                              {plot.ln_address}
                            </div>
                          </div>
                          <div className="plot-details">
                            <div className="plot-details-row">
                              {plot.ln_total_sqft_area} sqft
                              <br />
                              Plot Area
                            </div>
                            <div className="plot-details-row">
                              {plot.ln_plottype}
                            </div>
                            <div className="plot-details-row no-border">
                              {new Date(plot.ln_post_date).toLocaleDateString(
                                "en-US",
                                {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                }
                              )}
                              <br />
                              Posted on
                            </div>
                          </div>
                          <div className="image-wrap">
                            <Link to={`/${plot.ln_seo_url}`}>
                              <img
                                className="lazyload"
                                src={
                                  plot.image_files?.length > 0
                                    ? `${API_BASE}/Uploads/lands/${plot.image_files[0]}`
                                    : `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`
                                }
                                alt={plot.ln_title}
                              />
                            </Link>
                          </div>
                          <div className="content">
                            <h4 className="text_white">
                              <i className="bi bi-currency-rupee me-1"></i>
                              {formatShortPrice(plot.ln_price)}
                              <br />
                              <small>
                                <i className="bi bi-currency-rupee me-1"></i>
                                {formatNumberIndian(
                                  plot.ln_total_area_sqft
                                )}{" "}
                                per sqft
                              </small>
                            </h4>
                          </div>
                          <Link to={`/${plot.ln_seo_url}`} className="click">
                            <i className="bi bi-arrow-up-right-circle-fill"></i>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="carousel-bottom-controls">
                    <button
                      className="btn sw-button"
                      type="button"
                      data-bs-target="#singleCardCarousel"
                      data-bs-slide="prev"
                    >
                      <i className="icon-arrow-left-3"></i>
                    </button>
                    <button
                      className="btn sw-button"
                      type="button"
                      data-bs-target="#singleCardCarousel"
                      data-bs-slide="next"
                    >
                      <i className="icon-arrow-right-3"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Exclusive Owner Plots Section */}
        {exclusiveOwnerPlots.length > 0 && (
          <section className="section-listing tf-spacing-1">
            <div className="tf-container">
              <div className="row">
                <div className="col-12">
                  <div className="heading-section text-center mb-48">
                    <h2 className="title split-text effect-right">
                      Exclusive Owner Plots
                    </h2>
                  </div>
                  <div
                    className="swiper style-pagination tf-sw-mobile"
                    data-screen="992"
                    data-preview="1"
                    data-space="15"
                  >
                    <div className="swiper-wrapper tf-layout-mobile-lg lg-col-2">
                      {exclusiveOwnerPlots.slice(0, 4).map((plot, index) => (
                        <div className="swiper-slide" key={plot.ln_id}>
                          <div className="box-house hover-img style-list">
                            <div className="image-wrap">
                              <Link to={`/${plot.ln_seo_url}`}>
                                <img
                                  className="lazyload"
                                  src={
                                    plot.image_files?.length > 0
                                      ? `${API_BASE}/uploads/lands/${plot.image_files[0]}`
                                      : `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`
                                  }
                                  alt={plot.ln_title}
                                />
                              </Link>
                              <ul className="box-tag flex gap-8">
                                <li className="flat-tag text-4 bg-main fw-6 text_white">
                                  For Sale
                                </li>
                              </ul>
                            </div>
                            <div
                              className={`plot-details4 sec${(index % 5) + 1}`}
                            >
                              <div className="plot-details-row">
                                {plot.ln_total_sqft_area} sqft
                                <br />
                                Plot Area
                              </div>
                              <div className="plot-details-row">
                                {plot.ln_plottype}
                              </div>
                              <div className="plot-details-row no-border">
                                {new Date(plot.ln_post_date).toLocaleDateString(
                                  "en-US",
                                  {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                  }
                                )}
                                <br />
                                Posted on
                              </div>
                            </div>
                            <div className="content pt-4 pt-md-0">
                              <div className="row">
                                <div className="col-lg-9 col-8">
                                  <div className="plot-location">
                                    <i className="bi bi-geo-alt me-1"></i>
                                    <div className="plot-location-text ms-2">
                                      {plot.ln_locality},
                                      <br />
                                      {plot.ln_address}
                                    </div>
                                  </div>
                                  <p className="location text-1 line-clamp-1 mb-3">
                                    {plot.ln_title}
                                  </p>
                                  {plot.ln_corner === "Yes" && (
                                    <span className="highlights">
                                      Corner Plot
                                    </span>
                                  )}{" "}
                                  {plot.ln_nearby_landmark && (
                                    <span className="highlights">
                                      Close to {plot.ln_nearby_landmark}
                                    </span>
                                  )}
                                </div>
                                <div className="col-lg-3 col-4">
                                  <div className="circle-wrapper">
                                    {/* Rotating circular text */}
                                    <svg
                                      className="rotating-text"
                                      viewBox="0 0 100 100"
                                    >
                                      <defs>
                                        <path
                                          id="circlePath"
                                          d="M50,50 m-30,0 a30,30 0 1,1 60,0 a30,30 0 1,1 -60,0"
                                        />
                                      </defs>
                                      <text>
                                        <textPath
                                          href="#circlePath"
                                          startOffset="50%"
                                          textAnchor="middle"
                                        >
                                          View Details • View Details •
                                        </textPath>
                                      </text>
                                    </svg>

                                    {/* Center circle with arrow as link */}
                                    <div className="center-button">
                                      <Link to={`/${plot.ln_seo_url}`}>
                                        <i className="bi bi-arrow-up-right"></i>
                                      </Link>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="bot pt-3">
                                <h4 className="price">
                                  <i className="bi bi-currency-rupee me-1"></i>
                                  {formatShortPrice(plot.ln_price)}

                                  <br />
                                </h4>
                                <p className="text-dark">
                                  <i className="bi bi-currency-rupee me-1"></i>
                                  {formatNumberIndian(
                                    plot.ln_total_area_sqft
                                  )}{" "}
                                  per sqft
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      className="sw-pagination sw-pagination-mb text-center mt-20 d-lg-none d-block"
                      style={{ marginTop: 100 }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Why Choose Us Section */}
        <section className="section-trusted tf-spacing-1">
          <div className="tf-container">
            <div className="row relative">
              <div className="col-xl-9">
                <div className="content relative z-5">
                  <div className="heading-section mb-48">
                    <h2 className="title split-text effect-right">
                      Why Choose Us?
                    </h2>
                  </div>
                  <div className="tf-grid-layout sm-col-2">
                    <div
                      className="icons-box style-3 wow animate__zoomIn animate__ animated"
                      data-wow-duration="1.5s"
                      data-wow-delay="0s"
                    >
                      <div className="tf-icon sec1 shadow">
                        <i className="fa fa-users" aria-hidden="true"></i>
                      </div>
                      <div className="content">
                        <h5 className="title">Experienced Team</h5>
                        <p className="text-1">
                          Lorem ipsum dolor sit amet, consectetur adipiscing
                          elit, sed do eiusmod tempor incididunt ut labore et
                          dolore magna aliqua.
                        </p>
                      </div>
                    </div>
                    <div
                      className="icons-box style-3 wow animate__zoomIn animate__ animated"
                      data-wow-duration="1.5s"
                      data-wow-delay="0s"
                    >
                      <div className="tf-icon sec2 shadow">
                        <i className="fa fa-tasks" aria-hidden="true"></i>
                      </div>
                      <div className="content">
                        <h5 className="title">User-Friendly Interface</h5>
                        <p className="text-1">
                          Lorem ipsum dolor sit amet, consectetur adipiscing
                          elit, sed do eiusmod tempor incididunt ut labore et
                          dolore magna aliqua.
                        </p>
                      </div>
                    </div>
                    <div
                      className="icons-box style-3 wow animate__zoomIn animate__ animated"
                      data-wow-duration="1.5s"
                      data-wow-delay="0s"
                    >
                      <div className="tf-icon sec3 shadow">
                        <i className="fa fa-inr" aria-hidden="true"></i>
                      </div>
                      <div className="content">
                        <h5 className="title">Pay Zero Commission</h5>
                        <p className="text-1">
                          Lorem ipsum dolor sit amet, consectetur adipiscing
                          elit, sed do eiusmod tempor incididunt ut labore et
                          dolore magna aliqua.
                        </p>
                      </div>
                    </div>
                    <div
                      className="icons-box style-3 wow animate__zoomIn animate__ animated"
                      data-wow-duration="1.5s"
                      data-wow-delay="0s"
                    >
                      <div className="tf-icon sec4 shadow">
                        <i className="far fa-clock" aria-hidden="true"></i>
                      </div>
                      <div className="content">
                        <h5 className="title">Save Time and Money</h5>
                        <p className="text-1">
                          Lorem ipsum dolor sit amet, consectetur adipiscing
                          elit, sed do eiusmod tempor incididunt ut labore et
                          dolore magna aliqua.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-3">
                <br />
                <br />
                <br />
                <br />
                <br />
                <div className="activity-card bg-white">
                  {/* User Section */}
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-circle me-2">
                      <i className="fas fa-user"></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-semibold">
                        {isLoggedIn ? userName : "Guest User"}
                      </h6>
                      {isLoggedIn && <p>{getUserTypeLabel()}</p>}
                    </div>
                  </div>

                  <p className="fw-semibold mb-3 pt-4">Your Recent Activity</p>

                  {isLoggedIn ? (
                    <>
                      <div className="d-flex justify-content-between">
                        <div className="view-box mb-3 w-50 position-relative me-1">
                          <div className="arrow-icon">
                            <a href="#">
                              <i className="bi bi-arrow-up-right"></i>
                            </a>
                          </div>
                          <h4 className="mb-0">{getRecentViewsCount()}</h4>
                          <p className="text-1">Viewed</p>
                          {getSessionViewsCount() > 0 && (
                            <small className="text-muted d-block">
                              +{getSessionViewsCount()} today
                            </small>
                          )}
                        </div>

                        <div className="view-box mb-3 w-50 position-relative ms-1">
                          <div className="arrow-icon">
                            <a href="#">
                              <i className="bi bi-arrow-up-right"></i>
                            </a>
                          </div>
                          <h4 className="mb-0">{wishlistCount}</h4>
                          <p className="text-1">Saved</p>
                        </div>
                      </div>

                      <Link
                        to={
                          userType === 1 || userType === 2
                            ? "/authuser/My-Lands"
                            : "/authuser/activities"
                        }
                        className="tf-btn style-border pd-5 mx-auto my-4 px-5"
                      >
                        View All Activities
                      </Link>
                    </>
                  ) : (
                    <>
                      <div className="view-box mb-3 position-relative">
                        <div className="arrow-icon">
                          <Link to="/authuser/login">
                            <i className="bi bi-arrow-up-right"></i>
                          </Link>
                        </div>
                        <h4 className="mb-0">{getRecentViewsCount()}</h4>
                        <p className="text-1">Viewed</p>
                        {getSessionViewsCount() > 0 && (
                          <small className="text-muted d-block">
                            +{getSessionViewsCount()} today
                          </small>
                        )}
                      </div>

                      <Link
                        to="/authuser/login"
                        className="tf-btn style-border pd-5 mx-auto my-4 px-5 glow-blink"
                      >
                        Login/Register to Save Activity
                      </Link>
                      <p className="footer-note text-center">
                        & see your activities across browsers & devices...
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div
              className="person absolute wow animate__fadeInRight animate__animated"
              data-wow-duration="2s"
            >
              <img
                src={`${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/items/section-trusted-1.png`}
                alt=""
              />
            </div>
            <div
              className="item absolute wow animate__fadeInRight animate__animated"
              data-wow-duration="2s"
              data-wow-delay="0.3s"
            >
              <img
                src={`${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/items/section-trusted-2.png`}
                alt=""
              />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <div className="cta hero">
          <div
            className="tile tile-2 wow animate__fadeInRight animate__animated"
            data-wow-delay="1s"
          ></div>
          <div
            className="tile tile-1 wow animate__fadeInRight animate__animated"
            data-wow-delay="1.5s"
          ></div>
          <div
            className="tile tile-4 wow animate__fadeInLeft animate__animated"
            data-wow-delay="1s"
          ></div>
          <div
            className="tile tile-3 wow animate__fadeInLeft animate__animated"
            data-wow-delay="1.5s"
          ></div>
          <div className="tf-container cta-gap">
            <div className="row">
              <div className="col-12 col-md-6 pb-5">
                <div className="tf-grid-layout">
                  <div
                    className="icons-box shadow-none default effec-icon animate__zoomIn wow animate__animated"
                    data-wow-duration="1.5s"
                    data-wow-delay="2s"
                  >
                    <h3 className="title text-center text-md-start">
                      Looking for selling your Land?
                      <br /> Begin with us
                    </h3>
                    <p className="text-1 text-dark text-center text-md-start">
                      Start your search from here
                    </p>
                    <div className="d-flex justify-content-center justify-content-md-start">
                      <Link to="/lands" className="tf-btn style-border pd-5">
                        Explore Buying
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="tf-grid-layout">
                  <div
                    className="icons-box shadow-none default effec-icon animate__zoomIn wow animate__animated"
                    data-wow-duration="1.5s"
                    data-wow-delay="2s"
                  >
                    <h3 className="title text-center text-md-end">
                      Looking for a Land?
                      <br /> Go no where else{" "}
                    </h3>
                    <p className="text-1 text-dark text-center text-md-end">
                      Start your search from here
                    </p>
                    <div className="d-flex justify-content-center justify-content-md-end">
                      <Link
                        to="/authuser/login"
                        className="tf-btn style-border pd-5 float-end"
                      >
                        Explore Selling
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cities Section */}
        <section className="section-neighborhoods pb-0 tf-spacing-1">
          <div className="tf-container">
            <div className="col-12">
              <div className="heading-section text-center mb-48">
                <h2 className="title split-text effect-right">
                  Trending Properties in Top Cities
                </h2>
              </div>
              <div className="row justify-content-center g-3">
                <div className="col-auto">
                  <button
                    className="btn city-btn sec1"
                    onClick={() => handleCityClick("Bhubaneswar")}
                  >
                    Bhubaneswar
                  </button>
                </div>
                <div className="col-auto">
                  <button
                    className="btn city-btn sec2"
                    onClick={() => handleCityClick("Cuttack")}
                  >
                    Cuttack
                  </button>
                </div>
                <div className="col-auto">
                  <button
                    className="btn city-btn sec3"
                    onClick={() => handleCityClick("Puri")}
                  >
                    Puri
                  </button>
                </div>
                <div className="col-auto">
                  <button
                    className="btn city-btn sec4"
                    onClick={() => handleCityClick("Brahmapur")}
                  >
                    Brahmapur
                  </button>
                </div>
                <div className="col-auto">
                  <button
                    className="btn city-btn sec1"
                    onClick={() => handleCityClick("Sambalpur")}
                  >
                    Sambalpur
                  </button>
                </div>
                <div className="col-auto">
                  <button
                    className="btn city-btn sec2"
                    onClick={() => handleCityClick("Rourkela")}
                  >
                    Rourkela
                  </button>
                </div>
                <div className="col-auto">
                  <button
                    className="btn city-btn sec3"
                    onClick={() => handleCityClick("Baleswar")}
                  >
                    Baleswar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        {testimonials.length > 0 && (
          <div className="section-testimonials tf-spacing-1">
            <div className="tf-container">
              <div className="row">
                <div className="col-12">
                  <div className="heading-section text-center mb-48">
                    <h2 className="title split-text effect-right text-white">
                      Clients Testimonials
                    </h2>
                  </div>
                  {/* Slider 2 */}
                  <div className="d-none d-sm-block">
                    <div
                      id="multiCarousel2"
                      className="carousel slide"
                      data-bs-ride="carousel"
                    >
                      <div className="carousel-inner">
                        {(() => {
                          // Group testimonials into sets of three
                          const groupedTestimonials = [];
                          for (let i = 0; i < testimonials.length; i += 3) {
                            groupedTestimonials.push(
                              testimonials.slice(i, i + 3)
                            );
                          }
                          return groupedTestimonials.map(
                            (group, groupIndex) => (
                              <div
                                className={`carousel-item ${
                                  groupIndex === 0 ? "active" : ""
                                }`}
                                key={groupIndex}
                              >
                                <ul className="carousel-list">
                                  {group.map((t) => (
                                    <li key={t.tm_id}>
                                      <div className="wg-testimonial text-center">
                                        <div className="avatar">
                                          <img
                                            src={`${API_BASE}/Uploads/testimonials/${t.tm_image}`}
                                            alt={t.tm_name}
                                          />
                                        </div>
                                        <p
                                          className="text-1 description"
                                          dangerouslySetInnerHTML={{
                                            __html: t.tm_msg,
                                          }}
                                        ></p>
                                        <div className="ratings justify-center">
                                          {renderStars(t.rating)}
                                        </div>
                                        <div className="author">
                                          <h6>
                                            <a href="#">{t.tm_name}</a>
                                          </h6>
                                          <p className="text-2">
                                            {t.tm_designation}
                                          </p>
                                        </div>
                                      </div>
                                    </li>
                                  ))}
                                  {/* Optional: Pad with empty li if group has fewer than 3 items */}
                                  {group.length < 3 &&
                                    Array.from({
                                      length: 3 - group.length,
                                    }).map((_, index) => (
                                      <li
                                        key={`empty-${groupIndex}-${index}`}
                                        className="d-none"
                                      ></li>
                                    ))}
                                </ul>
                              </div>
                            )
                          );
                        })()}
                      </div>

                      <div className="carousel-controls-center">
                        <button
                          className="carousel-control-prev swiper-button-prev sw-button nav-prev-layout"
                          type="button"
                          data-bs-target="#multiCarousel2"
                          data-bs-slide="prev"
                        >
                          <i className="icon-arrow-left-3"></i>
                        </button>
                        <button
                          className="carousel-control-next swiper-button-next sw-button nav-next-layout"
                          type="button"
                          data-bs-target="#multiCarousel2"
                          data-bs-slide="next"
                        >
                          <i className="icon-arrow-right-3"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mob-s d-block d-sm-none">
                    <div
                      id="cardCarousel2"
                      className="carousel slide"
                      data-bs-ride="carousel"
                    >
                      <div className="carousel-inner">
                        {testimonials.map((t, index) => (
                          <div
                            className={`carousel-item ${
                              index === 0 ? "active" : ""
                            }`}
                            key={t.tm_id}
                          >
                            <div className="wg-testimonial text-center">
                              <div className="avatar">
                                <img
                                  src={`${API_BASE}/Uploads/testimonials/${t.tm_image}`}
                                  className="card-img-top"
                                  alt={t.tm_name}
                                />
                              </div>
                              <div className="card-body">
                                <h5 className="card-title">{t.tm_name}</h5>
                                <p
                                  className="text-1 description"
                                  dangerouslySetInnerHTML={{ __html: t.tm_msg }}
                                ></p>
                                <div className="ratings justify-content-center">
                                  {renderStars(t.rating)}
                                </div>
                                <p className="text-2 mt-2">
                                  {t.tm_designation}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="carousel-bottom-controls">
                        <button
                          className="btn carousel-control-prev swiper-button-prev sw-button"
                          type="button"
                          data-bs-target="#cardCarousel2"
                          data-bs-slide="prev"
                        >
                          <i className="icon-arrow-left-3"></i>
                        </button>
                        <button
                          className="btn carousel-control-prev swiper-button-next sw-button"
                          type="button"
                          data-bs-target="#cardCarousel2"
                          data-bs-slide="next"
                        >
                          <i className="icon-arrow-right-3"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Posts Section */}
        <section className="section-opinion tf-spacing-1 pt-0">
          <div className="tf-container">
            <div className="row">
              <div className="col-12">
                <div className="heading-section text-center mb-48">
                  <h2 className="title split-text effect-right">
                    Recent Blogs
                  </h2>
                </div>
              </div>
            </div>
            <div className="row">
              {blogs.slice(0, 3).map((blog) => (
                <div className="col-12 col-md-4" key={blog.b_id}>
                  <div className="blog-article-item style-2 hover-img">
                    <div className="image-wrap">
                      <Link to={`/blogs/${blog.b_pageurl}`}>
                        <img
                          className="lazyload"
                          data-src={`${API_BASE}/Uploads/blogs/${blog.b_img}`}
                          src={`${API_BASE}/Uploads/blogs/${blog.b_img}`}
                          alt={blog.b_title}
                        />
                      </Link>
                      <div className="box-tag">
                        <Link to={`/blogs/${blog.b_pageurl}`}>
                          <i className="bi bi-arrow-up-right-circle-fill"></i>
                        </Link>
                      </div>
                    </div>
                    <div className="article-content">
                      <div className="time">
                        <div className="icons">
                          <i className="icon-clock"></i>
                        </div>
                        <p className="fw-5">
                          {new Date(blog.b_postdt).toLocaleDateString("en-US", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                      <h4 className="title">
                        <Link
                          to={`/blogs/${blog.b_pageurl}`}
                          className="line-clamp-2"
                        >
                          {blog.b_title}
                        </Link>
                      </h4>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="row">
              <div className="col-12">
                <div className="heading-section text-center pt-3 mb-48">
                  <Link
                    to="/blogs"
                    className="tf-btn bg-color-primary pd-3 d-flex mx-auto"
                  >
                    View All
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </FrontendLayout>
  );
};

export default HomePage;
